import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { Property, MultilingualText } from '../types';
import { ChevronDown, ChevronUp, Video } from 'lucide-react';

interface PropertyFactsAndFeaturesProps {
  property: Property;
}

// Collapsible section with blue header bar
const CollapsibleSection: React.FC<{
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, defaultOpen = true, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-blue-600 text-white rounded-t-lg px-5 py-3 flex items-center justify-between cursor-pointer hover:bg-blue-700 transition-colors"
      >
        <h2 className="text-lg font-bold">{title}</h2>
        {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
      </button>
      {isOpen && (
        <div className="border border-t-0 border-gray-200 rounded-b-lg px-5 py-4 bg-white">
          {children}
        </div>
      )}
    </div>
  );
};

const PLACEHOLDER = '—';

const PropertyFactsAndFeatures: React.FC<PropertyFactsAndFeaturesProps> = ({ property }) => {
  const { t } = useTranslation();
  const { getTextInCurrentLanguage } = useLanguage();

  // Helper to render a translatable value (yes/no/ask/condition/orientation/etc.)
  const renderTranslatableValue = (value: string): string => {
    if (value === 'ask') return t('propertyValues.ask');
    if (value === 'n/a') return t('propertyValues.notApplicable');
    if (value === 'yes') return t('propertyValues.yes');
    if (value === 'no') return t('propertyValues.no');
    const translated = t(`propertyValues.${value}`, { defaultValue: '' });
    if (translated) return translated;
    return value;
  };

  // Translate propertyType to localized label
  const translatePropertyType = (type?: string): string => {
    if (!type) return PLACEHOLDER;
    const key = type.toLowerCase();
    const translated = t(`propertyTypes.${key}`, { defaultValue: '' });
    return translated || type;
  };

  // Translate listingType to localized label
  const translateListingType = (type?: string): string => {
    if (!type) return PLACEHOLDER;
    const typeMap: Record<string, string> = {
      'for sale': 'forSale',
      'sale': 'forSale',
      'for rent': 'forRent',
      'rent': 'forRent',
    };
    const key = typeMap[type.toLowerCase()];
    if (key) return t(`listingTypes.${key}`);
    return type;
  };

  // Helper to render a MultilingualText array as comma-separated string, or placeholder
  const renderMultilingualList = (items?: MultilingualText[]): string => {
    if (!items?.length) return PLACEHOLDER;
    return items.map(item => getTextInCurrentLanguage(item)).join(', ');
  };

  // Helper to render a single MultilingualText, or placeholder
  const getTextOrPlaceholder = (text?: MultilingualText): string => {
    if (!text) return PLACEHOLDER;
    return getTextInCurrentLanguage(text);
  };

  // Helper to convert number to string, or undefined for placeholder handling
  const numStr = (val?: number | null): string | undefined => {
    return val != null ? String(val) : undefined;
  };

  // Detail row: bullet + label: value (shows placeholder when value is missing)
  const DetailRow: React.FC<{ label: string; value: string | undefined; translate?: boolean }> = ({ label, value, translate }) => {
    const displayValue = value ? (translate ? renderTranslatableValue(value) : value) : PLACEHOLDER;
    return (
      <li className="py-1.5 text-sm">
        <span className="text-gray-600">{label}:</span>{' '}
        <span className="text-gray-900 font-medium">{displayValue}</span>
      </li>
    );
  };

  // Subcategory header + content
  const Subcategory: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="mb-4">
      <h3 className="font-bold text-gray-900 mb-1">{title}</h3>
      <ul className="list-disc list-inside space-y-0.5">
        {children}
      </ul>
    </div>
  );

  // --- INTERIOR SECTION ---
  const renderInterior = () => {
    const sections: React.ReactNode[] = [];

    // Bedrooms & Bathrooms
    sections.push(
      <Subcategory key="bedBath" title={t('factsFeatures.bedroomsAndBathrooms')}>
        <DetailRow label={t('factsFeatures.bedrooms')} value={numStr(property.beds)} />
        <DetailRow label={t('factsFeatures.bathrooms')} value={numStr(property.baths)} />
        <DetailRow label={t('factsFeatures.fullBathrooms')} value={numStr(property.fullBaths)} />
        <DetailRow label={t('factsFeatures.halfBathrooms')} value={numStr(property.halfBaths)} />
      </Subcategory>
    );

    // Heating
    sections.push(
      <Subcategory key="heating" title={t('factsFeatures.heating')}>
        <DetailRow label={t('factsFeatures.heating')} value={renderMultilingualList(property.heatingTypes)} />
      </Subcategory>
    );

    // Cooling
    sections.push(
      <Subcategory key="cooling" title={t('factsFeatures.cooling')}>
        <DetailRow label={t('factsFeatures.cooling')} value={renderMultilingualList(property.coolingTypes)} />
      </Subcategory>
    );

    // Appliances
    sections.push(
      <Subcategory key="appliances" title={t('factsFeatures.appliancesSection')}>
        <DetailRow label={t('factsFeatures.includedAppliances')} value={renderMultilingualList(property.appliances)} />
        <DetailRow label={t('factsFeatures.laundry')} value={getTextOrPlaceholder(property.laundryFeatures)} />
      </Subcategory>
    );

    // Features (interior features + flooring)
    sections.push(
      <Subcategory key="features" title={t('factsFeatures.interiorFeaturesSection')}>
        <DetailRow label={t('factsFeatures.interiorFeaturesSection')} value={renderMultilingualList(property.interiorFeatures)} />
        <DetailRow label={t('factsFeatures.flooring')} value={renderMultilingualList(property.flooring)} />
        <DetailRow label={t('property.terraceArea')} value={property.terraceArea != null ? `${property.terraceArea} m²` : undefined} />
      </Subcategory>
    );

    // Interior Area
    sections.push(
      <Subcategory key="area" title={t('factsFeatures.interiorArea')}>
        <DetailRow label={t('factsFeatures.totalStructureArea')} value={property.sqft != null ? property.sqft.toLocaleString() : undefined} />
        <DetailRow label={t('factsFeatures.livableArea')} value={property.livableArea != null ? `${property.livableArea.toLocaleString()} m²` : undefined} />
      </Subcategory>
    );

    // Video & Virtual Tour (conditional — these are links, not factual fields)
    const tourItems: React.ReactNode[] = [];
    if (property.virtualTourUrl) {
      tourItems.push(
        <li key="vtour" className="py-1.5 text-sm">
          <a href={property.virtualTourUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
            {t('factsFeatures.viewVirtualTour')}
          </a>
        </li>
      );
    }
    if (property.youtubeVideoUrls?.length) {
      property.youtubeVideoUrls.forEach((url, i) => {
        tourItems.push(
          <li key={`video-${i}`} className="py-1.5 text-sm flex items-center gap-1">
            <Video className="h-4 w-4 text-red-600 inline" />
            <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
              {t('factsFeatures.viewVideoTour')}{property.youtubeVideoUrls!.length > 1 ? ` ${i + 1}` : ''}
            </a>
          </li>
        );
      });
    }
    if (tourItems.length > 0) {
      sections.push(
        <Subcategory key="tours" title={t('factsFeatures.videoAndTour')}>
          {tourItems}
        </Subcategory>
      );
    }

    // Additional Features (conditional — these are pills)
    if (property.features?.length) {
      sections.push(
        <div key="addlFeatures" className="mb-4">
          <h3 className="font-bold text-gray-900 mb-2">{t('factsFeatures.additionalFeatures')}</h3>
          <div className="flex flex-wrap gap-2">
            {property.features.map((feature, index) => (
              <span
                key={index}
                className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium"
              >
                {getTextInCurrentLanguage(feature)}
              </span>
            ))}
          </div>
        </div>
      );
    }

    return sections;
  };

  // --- PROPERTY SECTION ---
  const renderProperty = () => {
    const sections: React.ReactNode[] = [];

    // Parking
    sections.push(
      <Subcategory key="parking" title={t('factsFeatures.parkingSection')}>
        <DetailRow label={t('factsFeatures.totalSpaces')} value={numStr(property.parking)} />
        <DetailRow label={t('factsFeatures.parkingFeatures')} value={renderMultilingualList(property.parkingFeatures)} />
        <DetailRow label={t('factsFeatures.garageType')} value={property.garageType ? t(`factsFeatures.${property.garageType}`) : undefined} />
        <DetailRow label={t('factsFeatures.coveredSpaces')} value={numStr(property.coveredSpaces)} />
      </Subcategory>
    );

    // Property Features
    sections.push(
      <Subcategory key="propFeatures" title={t('factsFeatures.propertyFeatures')}>
        <DetailRow label={t('factsFeatures.stories')} value={numStr(property.stories)} />
        <DetailRow label={t('property.totalFloors')} value={numStr(property.totalFloors)} />
        <DetailRow label={t('property.floor')} value={numStr(property.floor)} />
        <DetailRow label={t('factsFeatures.patioAndPorch')} value={renderMultilingualList(property.patioAndPorch)} />
        <DetailRow label={t('factsFeatures.fencing')} value={getTextOrPlaceholder(property.fencing)} />
        <DetailRow label={t('property.orientation')} value={property.orientation} translate />
        <DetailRow label={t('property.gatedCommunity')} value={property.gatedCommunity} translate />
        <DetailRow label={t('property.streetFrontage')} value={property.streetFrontage} translate />
        <DetailRow label={t('property.layout')} value={property.layout} translate />
        <DetailRow label={t('property.petsAllowed')} value={property.petsAllowed} translate />
        <DetailRow label={t('property.officeSuitable')} value={property.officeSuitable} translate />
        <DetailRow label={t('property.penthouse')} value={property.isPenthouse != null ? (property.isPenthouse ? 'yes' : 'no') : undefined} translate />
      </Subcategory>
    );

    // Lot
    sections.push(
      <Subcategory key="lot" title={t('factsFeatures.lotSection')}>
        <DetailRow label={t('factsFeatures.lotSize')} value={property.lotSize} />
        <DetailRow label={t('factsFeatures.lotFeatures')} value={renderMultilingualList(property.lotFeatures)} />
      </Subcategory>
    );

    // Details
    sections.push(
      <Subcategory key="details" title={t('factsFeatures.detailsSection')}>
        <DetailRow label={t('factsFeatures.additionalStructures')} value={renderMultilingualList(property.additionalStructures)} />
        <DetailRow label={t('property.socialHousing')} value={property.socialHousing} translate />
        <DetailRow label={t('property.minimumContract')} value={property.minimumContract} translate />
        <DetailRow label={t('property.financingAvailable')} value={property.financingAvailable} translate />
        <DetailRow label={t('common.hoaFees')} value={property.hoaFees != null ? `$${property.hoaFees}/month` : undefined} />
      </Subcategory>
    );

    return sections;
  };

  // --- CONSTRUCTION SECTION ---
  const renderConstruction = () => {
    const sections: React.ReactNode[] = [];

    // Type & Style (with translated propertyType and listingType)
    sections.push(
      <Subcategory key="typeStyle" title={t('factsFeatures.typeAndStyle')}>
        <DetailRow label={t('factsFeatures.homeType')} value={translatePropertyType(property.propertyType)} />
        <DetailRow label={t('factsFeatures.propertySubType')} value={getTextOrPlaceholder(property.propertySubType)} />
        <DetailRow label={t('factsFeatures.architecturalStyle')} value={getTextOrPlaceholder(property.architecturalStyle)} />
        <DetailRow label={t('common.listingType')} value={translateListingType(property.listingType)} />
      </Subcategory>
    );

    // Materials
    sections.push(
      <Subcategory key="materials" title={t('factsFeatures.materialsSection')}>
        <DetailRow label={t('factsFeatures.constructionMaterials')} value={renderMultilingualList(property.constructionMaterials)} />
        <DetailRow label={t('factsFeatures.foundation')} value={getTextOrPlaceholder(property.foundation)} />
        <DetailRow label={t('factsFeatures.roofType')} value={getTextOrPlaceholder(property.roof)} />
      </Subcategory>
    );

    // Condition
    sections.push(
      <Subcategory key="condition" title={t('factsFeatures.conditionSection')}>
        <DetailRow label={t('property.condition')} value={property.condition} translate />
        <DetailRow label={t('factsFeatures.yearBuilt')} value={numStr(property.yearBuilt)} />
        <DetailRow label={t('factsFeatures.newConstruction')} value={property.isNewConstruction != null ? (property.isNewConstruction ? 'yes' : 'no') : undefined} translate />
      </Subcategory>
    );

    return sections;
  };

  return (
    <div className="space-y-2">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('factsFeatures.title')}</h2>

      <CollapsibleSection title={t('factsFeatures.interior')}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          {renderInterior()}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title={t('factsFeatures.propertySection')}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          {renderProperty()}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title={t('factsFeatures.construction')}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          {renderConstruction()}
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default PropertyFactsAndFeatures;
